import type { CharacterScales } from "../domain/types";
import type { ProfileAnswers } from "../domain/answers";

/** UN8 only models binary sex for matching. */
export type Gender = "male" | "female";

export interface MatchScales {
  loyalty: number;
  honesty: number;
  passion: number;
  commitment: number;
}

/** Where a deck profile came from. */
export type ProfileSource = "pool" | "demo" | "self";

/** Profile shown in the matching deck (self or suggestion). */
export interface MatchProfile {
  id: string;
  firstName: string;
  age: number;
  city: string;
  /** ISO 3166-1 alpha-2 when known (e.g. DE). Empty/undefined = unanswered. */
  country?: string;
  /**
   * Hard filter for deck: "same" (own country), "any", or a specific ISO code.
   * Missing / "any" = no country gate.
   */
  preferredPartnerCountry?: "same" | "any" | string;
  gender: Gender;
  profession: string;
  aboutYou: string;
  lookingFor: string;
  hobbies: string;
  scales: MatchScales;
  /** Optional full-bleed portrait URL (demo stock or live media). */
  photoUrl?: string;
  /**
   * Demo only: seed a reverse like into interestStore when this profile is shown.
   * Not used as the sole mutual-match signal — real counter-likes live in interestStore.
   */
  likedYou?: boolean;
  /**
   * When they first arrived (ISO date or datetime). Demo seeds this;
   * live pool rows copy `enteredAt`. Missing = treat as new.
   */
  joinedAt?: string;
  /** Origin — approved applications use "pool". */
  source?: ProfileSource;
  /**
   * UN8 questionnaire, carried through from the application so the
   * engine can gate and score without a second lookup. Undefined for demo
   * profiles and for members who joined before the questionnaire shipped.
   */
  answers?: ProfileAnswers;
  /**
   * This profile's billing tier — paints the card, not the viewer's chrome.
   * Mapped through `tierOf()` to vip | paid | free. Missing = free.
   */
  plan?: string;
}

/** Persistent pool row after review approve. */
export interface MatchingPoolEntry {
  applicationId: string;
  profile: MatchProfile;
  enteredAt: string;
  updatedAt: string;
}

/**
 * UI-facing match row (viewer-centric).
 * Backed by MutualMatch in interestStore; profileId = the other person.
 */
export interface MatchRecord {
  id: string;
  profileId: string;
  matchedAt: string;
  valuesScore: number;
  chatUnlocked: boolean;
}

/**
 * Session / UI state for the local viewer (self + daily deck).
 * Likes/passes/matches persistence: interestStore (multi-user).
 * likedIds / passedIds / matches here are derived views + migration cache.
 */
export interface MatchState {
  self: MatchProfile | null;
  /** Derived from interestStore for current self (liked). */
  likedIds: string[];
  /** Derived from interestStore for current self (passed). */
  passedIds: string[];
  /** Derived mutual matches for current self. */
  matches: MatchRecord[];
  deckDate: string;
  deckIds: string[];
}

export function oppositeGender(g: Gender): Gender {
  return g === "male" ? "female" : "male";
}

export function scalesFromAnswers(
  scales: CharacterScales | Record<string, number | null>
): MatchScales {
  const clamp = (n: number) => Math.min(10, Math.max(1, Math.round(n)));
  // Social / creativity / humor stay on CharacterScales only. Matching is these four.
  return {
    loyalty: clamp(Number(scales.loyalty) || 5),
    honesty: clamp(Number(scales.honesty) || 5),
    passion: clamp(Number(scales.passion) || 5),
    commitment: clamp(Number(scales.commitment) || 5),
  };
}
